import { useEffect, useMemo, useState } from 'react';
import './shopmap.css';
import './mission.css';
import TrailMap from './TrailMap';
import MissionStepsList from './missionSteps';
import closeLeftShop from '../../assets/close_leftshop.png';
import closeRightShop from '../../assets/close_rightshop.png';
import openLeftShop from '../../assets/open_leftshop.png';
import openRightShop from '../../assets/open_rightshop.png';
import cafeImg from '../../assets/cafe.png';
import zini from '../../assets/neutral.png';
import { getActiveShopsForJourney } from '../../services/shopService';
import { getMissionStepsForJourney } from '../../services/missionStepService';
import { mapMissionStepsForUI } from './missionStepAdapter';

function ArrowLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M19 12H5M11 6l-6 6 6 6" stroke="#3d2f7a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
      <path d="M12 22s7-6.4 7-12a7 7 0 0 0-14 0c0 5.6 7 12 7 12Z" stroke="#6c4ce8" strokeWidth="2.1" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="2.4" stroke="#6c4ce8" strokeWidth="2.1" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M6 6l12 12M18 6L6 18" stroke="#3d2f7a" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M9 5l7 7-7 7" stroke="#ffffff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RewardIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M7 4h10v5a5 5 0 0 1-10 0V4Z"
        stroke="#3d2f7a"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M7 5H4v1a4 4 0 0 0 3.6 4M17 5h3v1a4 4 0 0 1-3.6 4" stroke="#3d2f7a" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 14v3M9 20h6M9.5 20c0-1.6.7-2.4 1.5-3h2c.8.6 1.5 1.4 1.5 3" stroke="#3d2f7a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M20 4v5h-5" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="3.6" stroke="#3d2f7a" strokeWidth="2" />
      <path d="M5 19c1.4-3.4 4-5 7-5s5.6 1.6 7 5" stroke="#3d2f7a" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// Genuine-API-failure fallback ONLY. This is never used when Laravel
// successfully responds, even with an empty shop list — it exists purely so
// the map isn't completely broken if the backend is unreachable.
const FALLBACK_SHOPS = ['Cafe Mocha', 'Brew House', 'Bean & Co.'].map((name, i) => ({
  id: i + 1,
  name,
}));

// Vertical space each stop gets on the trail canvas (px).
const STOP_SPACING = 168;

// Step 6B: a shop's own mission-aware state, from the authenticated user's
// restored userMissions (Step 5H, already loaded in App.js — no new API
// call here). Looked up by shop_id alone: user_missions has a unique
// (user_id, shop_id) constraint, so a shop's mission can never be another
// shop's and never needs journey scoping to be found unambiguously.
// "Completed" mirrors the same signal Step 5F's points() and
// journeyAdapter's isShopVisited() already use — status === 'completed' OR
// a real point_transactions row — never current_step alone, and never
// inferred from the global collectedStars count.
function getShopMissionState(userMissions, shopId) {
  const mission = (userMissions || []).find((m) => m.shop_id === shopId) || null;
  if (!mission) {
    return { mission: null, isCompleted: false, isInProgress: false };
  }
  const hasPointTransaction =
    Array.isArray(mission.point_transactions) && mission.point_transactions.length > 0;
  const isCompleted = mission.status === 'completed' || hasPointTransaction;
  return { mission, isCompleted, isInProgress: !isCompleted };
}

export default function ShopMap({
  onBack,
  onSelectShop,
  onJourney,
  onProfile,
  onSample,
  collectedStars = 0,
  userMissions = [],
  // The live journey for the user's chosen city (App.js resolves this via
  // getActiveJourneyForLocation). undefined while App.js is still resolving
  // it (shops stays in its loading state below), null once resolved to "no
  // live journey for this city yet", or the journey's real id.
  journeyId,
  // The chosen city's name (from that same resolved journey's location),
  // shown top-left so the user can see which city they're browsing.
  cityName,
}) {
  const dateRangeLabel = useMemo(() => {
    const rangeStart = new Date();
    const rangeEnd = new Date();
    rangeEnd.setDate(rangeStart.getDate() + 6);
    const format = (date) =>
      `${date.getDate()} ${date.toLocaleDateString('en-US', { month: 'short' })}`;
    return `${format(rangeStart)} - ${format(rangeEnd)}`;
  }, []);

  const [activeShop, setActiveShop] = useState(null);
  // null while the Laravel request is in flight; an array (possibly empty)
  // once it resolves. Kept separate from FALLBACK_SHOPS so a successful "no
  // shops yet" response is never confused with a load failure.
  const [shops, setShops] = useState(null);
  // Bumped by the empty state's Refresh button to re-fetch the shop list.
  const [refreshKey, setRefreshKey] = useState(0);
  // Mission steps for the currently-open modal's journey. null while
  // loading/no modal open, an array (possibly empty) once resolved for
  // activeShop.journey_id.
  const [missionSteps, setMissionSteps] = useState(null);

  useEffect(() => {
    // journeyId undefined -> App.js hasn't resolved the city's live journey
    // yet; keep shops at its initial null (loading) instead of fetching
    // anything premature.
    if (journeyId === undefined) return undefined;

    // journeyId null -> resolved: this city currently has no live journey.
    // An empty, genuinely-loaded shop list (not the network-failure
    // fallback below), so the empty state renders correctly.
    if (journeyId === null) {
      setShops([]);
      return undefined;
    }

    let cancelled = false;

    getActiveShopsForJourney(journeyId)
      .then((activeShops) => {
        if (!cancelled) setShops(activeShops);
      })
      .catch((err) => {
        console.error('ShopMap: failed to load shops from Laravel, using fallback list.', err);
        if (!cancelled) setShops(FALLBACK_SHOPS);
      });

    return () => {
      cancelled = true;
    };
  }, [journeyId, refreshKey]);

  // Loads the mission steps for the modal's shop whenever it opens/changes.
  // Reset to null (loading) immediately so the previous shop's steps never
  // flash while the new journey's steps are being fetched.
  useEffect(() => {
    if (!activeShop) {
      setMissionSteps(null);
      return undefined;
    }

    let cancelled = false;
    setMissionSteps(null);

    getMissionStepsForJourney(activeShop.journey_id)
      .then((steps) => {
        if (!cancelled) setMissionSteps(mapMissionStepsForUI(steps));
      })
      .catch((err) => {
        console.error('ShopMap: failed to load mission steps from Laravel.', err);
        if (!cancelled) setMissionSteps([]);
      });

    return () => {
      cancelled = true;
    };
  }, [activeShop]);

  const isLoading = shops === null;
  const shopList = shops || [];
  const isEmpty = !isLoading && shopList.length === 0;

  const handleRefresh = () => {
    setShops(null);
    setRefreshKey((k) => k + 1);
  };

  // Shop illustrations are drawn bigger when there are only a few stops and
  // smaller as the list grows. Baseline 132px at 5 shops, +/-24px per shop,
  // clamped to a sane range.
  const shopImgWidth = Math.round(Math.min(200, Math.max(112, 132 + (5 - shopList.length) * 24)));

  // Step 6D: strict sequential unlock, no-skip. The unlock cursor must never
  // sit behind what the user's own restored missions already prove is
  // complete — walk shopList from the very start and count only shops that
  // are completed back-to-back from index 0 (the same isCompleted rule
  // Step 6C already uses: status === 'completed' OR a real point
  // transaction). The walk stops at the first not-yet-completed shop, so an
  // out-of-order completion (which the backend's own places_required rule
  // never actually produces) can never skip an earlier incomplete shop
  // ahead in this count.
  //
  // This is combined with the existing collectedStars-derived cursor via
  // Math.max rather than replacing it outright: collectedStars updates live
  // the instant a mission is completed in this same session (before that
  // shop's own entry may even exist yet in the userMissions snapshot loaded
  // at session start), while the mission-based count above is what's
  // correct immediately after a reload/re-login (when selectedJourneyId is
  // still null and Step 5I-D correctly zeroes collectedStars, but
  // userMissions has already been restored). Taking the larger of the two
  // only ever recognizes real, already-confirmed completions — it can never
  // advance the cursor past what either signal has actually confirmed.
  let consecutiveCompletedFromMissions = 0;
  for (let i = 0; i < shopList.length; i++) {
    if (!getShopMissionState(userMissions, shopList[i].id).isCompleted) break;
    consecutiveCompletedFromMissions += 1;
  }

  const positionalCollected = Math.max(0, Math.min(collectedStars, shopList.length));
  const safeCollected = Math.max(positionalCollected, consecutiveCompletedFromMissions);
  const activeIndex = Math.min(safeCollected, shopList.length - 1);

  const nodes = shopList.map((shop, i) => {
    // Step 6B: a shop the user has a real, specific mission for always
    // takes precedence over the positional guess below — this is what
    // makes a previously completed/in-progress shop show and behave
    // correctly right after re-login, instead of only the single
    // positionally-"next" shop ever being recognized.
    const { isCompleted, isInProgress } = getShopMissionState(userMissions, shop.id);
    const hasMission = isCompleted || isInProgress;

    // Positional sequential-unlock fallback — completely unchanged from
    // before, and used only when this shop has no restored mission of its
    // own, so a brand-new user (or a shop nobody has started yet) keeps the
    // exact existing behavior. Never used to override a shop's own real
    // mission state.
    const positionalDone = i < safeCollected;
    const positionalActive = i === activeIndex && !positionalDone;

    const isDone = isCompleted || (!hasMission && positionalDone);
    const isActive = isInProgress || (!hasMission && positionalActive);
    // Step 6C: a completed shop is done — never clickable again, never
    // reopening the mission preview, never able to repeat Discover/Selfie/
    // Post/Points. An in-progress shop's own mission keeps it clickable (to
    // resume). A shop with no mission keeps the exact existing clickability
    // rule — only the single positionally-active shop opens.
    const isClickable = isInProgress || (!hasMission && positionalActive);

    return {
      id: shop.id,
      state: isDone ? 'done' : isActive ? 'active' : 'locked',
      x: i % 2 === 0 ? 30 : 70,
      y: ((i + 0.5) / shopList.length) * 100,
      label: shop.name,
      ariaLabel: shop.name,
      // Any reachable stop (done, in progress, or the position Zini is
      // currently on) shows the open cafe; stops still genuinely locked
      // show the closed cafe.
      content: (
        <img
          src={
            i % 2 === 0
              ? (isDone || isActive ? openLeftShop : closeLeftShop)
              : (isDone || isActive ? openRightShop : closeRightShop)
          }
          alt={shop.name}
        />
      ),
      onClick: isClickable ? () => setActiveShop(shop) : undefined,
      disabled: !isClickable,
    };
  });

  // Bigger shop images need more vertical room between stops.
  const stopGap = Math.max(STOP_SPACING, shopImgWidth + 40);
  const canvasHeight = Math.max(shopList.length * stopGap, 520);

  // Step 6C: a completed shop is never clickable (see isClickable above), so
  // activeShop can only ever be a new or in-progress shop here — there is no
  // "completed" label/action in this modal at all.
  const activeShopMissionState = activeShop
    ? getShopMissionState(userMissions, activeShop.id)
    : { isInProgress: false };
  const missionButtonLabel = activeShopMissionState.isInProgress ? 'Resume' : 'Start';

  return (
    <div className="shopmap-screen">
      <div className={`shopmap-frame${isEmpty ? ' shopmap-frame--empty' : ''}`}>
        <div className="shopmap-topbar">
          {/* <div className="shopmap-topbar-left">
            <button type="button" className="shopmap-back-btn" aria-label="Go back" onClick={onBack}>
              <ArrowLeftIcon />
            </button>
          </div> */}
          {cityName && (
            <div className="shopmap-topbar-city">
              <PinIcon />
              <span className="shopmap-topbar-city-name">{cityName}</span>
            </div>
          )}
          {!isEmpty && (
            <div className="shopmap-topbar-date">
              <span className="shopmap-topbar-date-day">{dateRangeLabel}</span>
            </div>
          )}
          {/* <button type="button" className="shopmap-sample-btn" onClick={onSample}>
            Sample
          </button> */}
        </div>

        <div className="shopmap-side-actions">
          {!isEmpty && (
            <button type="button" className="shopmap-nav-btn" aria-label="Rewards" onClick={onJourney}>
              <RewardIcon />
            </button>
          )}
          <button type="button" className="shopmap-nav-btn" aria-label="My Profile" onClick={onProfile}>
            <ProfileIcon />
          </button>
        </div>

        {isLoading ? (
          <div className="shopmap-status">Loading shops&hellip;</div>
        ) : shopList.length === 0 ? (
          <div className="shopmap-empty">
            <img className="shopmap-empty-art" src={cafeImg} alt="" />
            <h2 className="shopmap-empty-title">No shops available yet</h2>
            <p className="shopmap-empty-text">
              We&rsquo;re working on bringing amazing shops to your area. Check back soon!
            </p>
            <button type="button" className="shopmap-empty-refresh" onClick={handleRefresh}>
              <RefreshIcon />
              <span>Refresh</span>
            </button>
          </div>
        ) : (
          <div
            className="shopmap-scroll"
            style={{ '--shop-canvas-h': `${canvasHeight}px`, '--shop-img-w': `${shopImgWidth}px` }}
          >
            <TrailMap
              nodes={nodes}
              activeId={shopList[activeIndex].id}
              avatarSrc={zini}
              showPath
              roadPath
              className="shopmap-trail"
            />
          </div>
        )}
      </div>

      {activeShop && (
        <div className="shopmap-modal-backdrop" onClick={() => setActiveShop(null)}>
          <div className="shopmap-modal-card" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="shopmap-modal-close-btn"
              aria-label="Close"
              onClick={() => setActiveShop(null)}
            >
              <CloseIcon />
            </button>

            <h2 className="shopmap-modal-title">{activeShop.name}</h2>
            <p className="mission-subtitle">
              {missionSteps && missionSteps.length > 0
                ? `Complete all ${missionSteps.length} steps and unlock a surprise!`
                : 'Complete the mission and unlock a surprise!'}
            </p>

            {missionSteps === null ? (
              <p className="shopmap-status">Loading mission steps&hellip;</p>
            ) : missionSteps.length === 0 ? (
              <p className="shopmap-status">No mission steps configured for this journey yet.</p>
            ) : (
              <MissionStepsList steps={missionSteps} />
            )}

            <button type="button" className="mission-start-btn" onClick={() => onSelectShop?.(activeShop)}>
              {missionButtonLabel}
              <span className="mission-start-icon">
                <ChevronRightIcon />
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
